/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Link,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tag,
  TagLabel,
  Tooltip,
  Button,
  useDisclosure,
  useToast,
  Stack,
  Text,
  Box,
  VStack,
  HStack,
  Badge,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { getOpenOrders, getTrades } from '../../common/api';
import { toTron } from '../../common/utils';
import { BANDWIDTH, ENERGY } from '../../common/utils/constants';
import {
  initiateSocket,
  subscribeFinishedOrders,
} from '../../common/api/socket';
import isEmpty from 'is-empty';
import moment from 'moment';
import smartTruncate from 'smart-truncate';
import FilterIcon from '../../components/icons/filter';
import CapsuleProgress from '../../components/CapsuleProgress';
import FilledIcon from '../../components/icons/filter/filled';
import FillOrder from '../../components/FillOrder';
import { useSelector } from 'react-redux';
import logger from '../../utils/logger';
// Temporarily commenting out pagination to fix import error
// import {
//   Pagination,
//   PaginationContainer,
//   PaginationNext,
//   PaginationPage,
//   PaginationPageGroup,
//   PaginationPrevious,
//   usePagination,
// } from '@ajna/pagination';

export default function RecentTrades() {
  const { noWalletDetect, walletActivated } = useSelector(
    (state) => state.account
  );
  const toast = useToast();
  const {
    isOpen: isFillOrderOpen,
    onOpen: onFillOrderOpen,
    onClose: onFillOrderClose,
  } = useDisclosure();

  const [recentTrade, setRecentTrade] = useState([]);
  const [socket, setSocket] = useState();
  const [openOrders, setOpenOrders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [orderData, setOrderData] = useState(null);
  // Temporarily disable pagination
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (nextPage) => {
    setCurrentPage(nextPage);
  };

  const getTradesHandler = useCallback(async () => {
    try {
      const res = await getTrades('COMPLETED', currentPage);
      setRecentTrade(res.data || []);
      setTotalPages(res.totalPages || 1);
    } catch (error) {
      logger.error('Failed to fetch trades:', error);
      setRecentTrade([]);
      setTotalPages(1);
    }
  }, [currentPage]);

  useEffect(() => {
    getTradesHandler();
    
    // Initialize socket with error handling
    try {
      const socketTemp = initiateSocket();
      if (socketTemp) {
        setSocket(socketTemp);
        subscribeFinishedOrders((err, data) => {
          if (err) {
            logger.error('Socket subscription error:', err);
            return;
          }
          if (data) {
            setRecentTrade((recentTrade) => [data, ...recentTrade]);
          }
        });
      }
    } catch (error) {
      logger.error('Failed to initialize socket:', error);
    }
  }, [getTradesHandler]);

  const getOpenOrdersHandler = async (page) => {
    try {
      const res = await getOpenOrders(page);
      if (res && res.data) {
        res.data.forEach((el) => {
          el.isOpenOrder = true;
        });
        setOpenOrders(res.data);
      } else {
        setOpenOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch open orders:', error);
      setOpenOrders([]);
    }
  };

  useEffect(() => {
    getOpenOrdersHandler(1);
  }, []);

  const _rows = [...openOrders, ...recentTrade];

  function getPendingPercentage(resource_amount, resource_amount_ordered) {
    let pendingAmount = resource_amount_ordered - resource_amount;
    let percentage = (pendingAmount / resource_amount_ordered) * 100;
    return percentage;
  }

  const isMobile = useBreakpointValue({ base: true, md: false });
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <>
      {/* Single Responsive Table */}
      <Box overflowX={{ base: 'auto', md: 'visible' }} pb={{ base: 2, md: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          {!isEmpty(recentTrade) ? (
            <>
              <Table size={{ base: 'sm' }} minW={{ base: '650px', md: 'auto' }}>
                <Thead>
                  <Tr>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Resource</Th>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Price/day</Th>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Payout</Th>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Date</Th>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Fulfilled</Th>
                    <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                      <FilterIcon />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                {_rows?.map((e) => {
                  return (
                    <Tr key={e.txn_id + e._id}>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                        <Tag
                          size={{ base: 'sm', md: 'sm' }}
                          key={'sm'}
                          variant='outline'
                          colorScheme={
                            e.resource_type === ENERGY ? 'red' : 'teal'
                          }
                        >
                          <Tooltip
                            label={
                              e.resource_type === ENERGY ? ENERGY : BANDWIDTH
                            }
                            placement='auto-start'
                          >
                            <TagLabel>
                              {e.resource_type === ENERGY ? 'E' : 'B'}
                            </TagLabel>
                          </Tooltip>
                        </Tag>{' '}
                        {e.resource_amount_ordered}
                      </Td>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>{e.price_per_day} SUN</Td>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>{toTron(e.order_amount)} TRX</Td>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                        {moment(e.created_at).format('YYYY-MM-DD HH:mm:ss')}
                      </Td>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                        {' '}
                        <CapsuleProgress
                          value={
                            e?.isOpenOrder
                              ? getPendingPercentage(
                                  e.resource_amount,
                                  e.resource_amount_ordered
                                )
                              : 100
                          }
                        />
                      </Td>
                      <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                        {/* <Link
                          fontSize={{ base: 'sm', md: 'md' }}
                          href={`https://tronscan.org/#/transaction/${e.txn_id}`}
                          isExternal
                        >
                          {smartTruncate(e.txn_id, 18, {
                            position: 8,
                          })}
                        </Link> */}
                        {e?.isOpenOrder ? (
                          <Button
                            onClick={() => {
                              if (noWalletDetect) {
                                return toast({
                                  title: `Wallet not connected`,
                                  position: 'top-right',
                                  status: 'error',
                                });
                              }
                              if (!walletActivated) {
                                return toast({
                                  title: `Wallet not activated`,
                                  position: 'top-right',
                                  status: 'error',
                                });
                              }
                              onFillOrderOpen();
                              setOrderData(e);
                            }}
                            className='sell-button'
                          >
                            Sell
                          </Button>
                        ) : (
                          <FilledIcon />
                        )}
                      </Td>
                    </Tr>
                  );
                }) ?? null}
              </Tbody>
            </Table>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <h3>No Data Available</h3>
            </div>
          )}
        </div>
      </Box>

            {/* Pagination temporarily disabled due to import issues */}
            {/* { !isEmpty(recentTrade) && totalPages > 1 ? (
              <Stack>
                <Pagination
                  pagesCount={pagesCount}
                  currentPage={currentPage}
                  isDisabled={isDisabled}
                  onPageChange={handlePageChange}
                >
                  <PaginationContainer
                    align='center'
                    justify='space-between'
                    p={4}
                    w='full'
                  >
                    <PaginationPrevious
                      _hover={{ bg: 'gray.300' }}
                      bg='gray.200'
                      onClick={() => getOpenOrdersHandler(currentPage - 1)}
                    >
                      <Text>Previous</Text>
                    </PaginationPrevious>
                    <PaginationPageGroup align='center'>
                      {pages.map((page) => (
                        <PaginationPage
                          w={7}
                          bg='rgba(247, 1, 18, 0.05)'
                          key={`pagination_page_${page}`}
                          page={page}
                          onClick={() => getOpenOrdersHandler(page)}
                          fontSize='sm'
                          _hover={{
                            bg: 'gray.300',
                          }}
                          _current={{
                            bg: 'rgba(241, 146, 153, 0.5)',
                            fontSize: 'sm',
                            w: 7,
                          }}
                        />
                      ))}
                    </PaginationPageGroup>
                    <PaginationNext
                      _hover={{ bg: 'gray.300' }}
                      bg='gray.200'
                      onClick={() => getOpenOrdersHandler(currentPage + 1)}
                    >
                      <Text>Next</Text>
                    </PaginationNext>
                  </PaginationContainer>
                </Pagination>
              </Stack>
            ) : null} */}

      <FillOrder
        isOpen={isFillOrderOpen}
        onClose={onFillOrderClose}
        orderData={orderData}
        setOrderData={setOrderData}
      />
    </>
  );
}
